/**
 * SNS Post Render
 *
 * Post-level SNS glue.
 * Render layer: DOM manipulation only.
 */

import { SNSPreviewLiveRender } from "../preview-live/sns-preview-live-render";
import { Roles } from "../../constants/roles";
import type { SnsPostPreviewElements } from "./sns-post-types.d.ts";

export class SNSPostRender {
    private static getSnsLinkItems(container: HTMLElement): HTMLElement[] {
        return Array.from(container.children).filter(
            (node): node is HTMLElement =>
                node instanceof HTMLElement && node.getAttribute("data-role") === Roles.SNS_LINK_ITEM,
        );
    }

    /** Create a single sns_links[] input row element. */
    public static createSnsLinkItem(params: { index: number; value?: string }): HTMLDivElement {
        const { index, value = "" } = params || {};

        const item = document.createElement("div");
        item.className = "sns-link-item";
        item.dataset.role = Roles.SNS_LINK_ITEM;
        item.dataset.index = String(typeof index === "number" ? index : 0);

        const input = document.createElement("input");
        input.type = "text";
        input.name = "sns_links[]";
        input.className = "textbox sns-link-input";
        input.dataset.role = Roles.SNS_LINK_INPUT;
        input.placeholder = "https://...";
        input.value = String(value || "");

        item.appendChild(input);
        return item;
    }

    /** Re-index all SNS link item elements under a list. */
    public static reindexSnsLinksList(snsLinksList: HTMLElement): void {
        const items = SNSPostRender.getSnsLinkItems(snsLinksList);
        items.forEach((item, idx) => {
            item.dataset.index = String(idx);
        });
    }

    /** Append a new sns_links[] row to the editor list. */
    public static appendSnsLinkItem(params: { snsLinksList: HTMLElement; value?: string; focus?: boolean }): void {
        const { snsLinksList, value = "", focus = true } = params || {};
        if (!snsLinksList) return;

        const newIndex = SNSPostRender.getSnsLinkItems(snsLinksList).length;
        const item = SNSPostRender.createSnsLinkItem({ index: newIndex, value });
        snsLinksList.appendChild(item);

        SNSPostRender.reindexSnsLinksList(snsLinksList);

        if (focus) {
            const input = item.firstElementChild;
            if (input) {
                try {
                    (input as HTMLInputElement).focus();
                } catch (_e) {
                    // ignore
                }
            }
        }
    }

    /** Remove a sns link row and keep indices consistent. */
    public static removeSnsLinkItem(item: HTMLElement): void {
        const parent = item?.parentElement;
        item?.remove?.();
        if (parent && parent instanceof HTMLElement) {
            SNSPostRender.reindexSnsLinksList(parent);
        }
    }

    /** Clear preview containers, including legacy YouTube and OGP containers. */
    public static clearSnsPreviewContainers(elements: SnsPostPreviewElements): void {
        const { youtubeContainer, snsContainer, ogpContainer, youtubeSection, ogpTabContainer } = elements || {};

        if (youtubeContainer) youtubeContainer.innerHTML = "";
        if (snsContainer) snsContainer.innerHTML = "";
        if (ogpContainer) ogpContainer.innerHTML = "";
        if (ogpTabContainer) ogpTabContainer.innerHTML = "";
        if (youtubeSection) youtubeSection.style.display = "none";
    }

    /** Render SNS preview tiles into the live preview container. */
    public static renderSnsPreviewTiles(params: { elements: SnsPostPreviewElements; urls: string[] }): void {
        const { elements, urls } = params;
        if (!elements?.snsContainer) return;

        SNSPreviewLiveRender.updatePreviewWithUrls(urls, elements.snsContainer);
    }

    /** Activate a preview tile from image-first state into its embed state. */
    public static activateSnsPreviewTile(tile: HTMLElement): void {
        SNSPreviewLiveRender.activateSnsPreviewTile(tile);
    }
}
